import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { TovpilCrudService } from './tovpil-crud-service';
import { FormGroup } from '@angular/forms';
import { Constants } from '../shared/vo/Constants';

@Injectable({
    providedIn: 'root'
})
export class DropdownSelectorService {

    /** ENDPOINTS */
    endPointGetStoreById = "getStoreByIdWithDescription";

    /**
     * The constructor function initializes private properties with ApiService and TovpilCrudService
     * dependencies.
     * @param {ApiService} apiService - The `apiService` parameter is likely a service that handles API
     * requests and responses in your application. It is injected into the constructor using Angular's
     * dependency injection system. This service may be responsible for making HTTP requests to a backend
     * server, handling authentication, and managing data from external APIs.
     * @param {TovpilCrudService} tovpilCrudService - The `tovpilCrudService` parameter in the constructor
     * is an instance of the `TovpilCrudService` class, which is likely a service used for CRUD operations
     * related to a specific entity or resource in the application. This service may provide methods for
     * creating, reading, updating,
     */
    constructor(
        private apiService: ApiService,
        private tovpilCrudService: TovpilCrudService
    ) { }

    /**
     * The `initLoadCoordinationSelector` function is an asynchronous function that initializes and loads
     * coordination selectors based on user data and preferences, handling errors and blocking UI during
     * the process.
     * @param {FormGroup} globalUserDataForm - The `globalUserDataForm` parameter is a FormGroup object
     * that contains the user data form with input fields and their values. It is used to validate the form
     * data before proceeding with the initialization process.
     * @param {any} globalUserPreferences - The `globalUserPreferences` parameter is an object containing
     * the user's preferences. It may have a `preferences` property which in turn may have a `levelId`
     * property. This `levelId` is used to determine whether to call `listDependentGuideStores` or
     * `loadCoordination
     * @param {'from' | 'to'} type - The `type` parameter in the `initLoadCoordinationSelector` function is
     * a string literal type that can have the value `'from'` or `'to'`. It is used to determine whether
     * the coordination selector is being initialized for the 'from' location or the 'to' location.
     * @param handleError - The `handleError` parameter is a function that takes a `message` of type string
     * as an argument. It is used to handle and display error messages to the user in case of invalid data
     * or any other errors that may occur during the initialization process.
     * @param setBlocked - The `setBlocked` parameter in the `initLoadCoordinationSelector` function is a
     * function that takes a boolean parameter and is used to set the blocked state. When
     * `setBlocked(true)` is called, it indicates that the process is blocked or in progress, and when
     * `setBlocked(false)`
     * @returns The `initLoadCoordinationSelector` function returns an array of items after performing
     * certain asynchronous operations based on the input parameters provided. If the globalUserDataForm is
     * not valid, it returns an empty array after displaying an error message. If the globalUserDataForm is
     * valid, it proceeds to fetch data either by calling `listDependentGuideStores` or
     *  functions based on
     */
    async initLoadCoordinationSelector(
        _storeId: string,
        globalUserDataForm: FormGroup,
        globalUserPreferences: any,
        type: 'from' | 'to' | 'base',
        handleError: (message: string) => void,
        setBlocked: (blocked: boolean) => void
    ): Promise<any[]> {
        if (!globalUserDataForm.valid) {
            handleError('Datos Inválidos, Por favor ingrese los datos solicitados!');
            return [];
        }

        setBlocked(true);

        try {
            const levelId = globalUserPreferences.preferences?.levelId ?? null;
            let catalogList = [];
            if (levelId == Constants.LOCAL_LEVELID) {
                catalogList = await this.listDependentGuideStores(globalUserPreferences, type);
            } else {
                catalogList = await this.loadCoordinationCatalogs(_storeId, globalUserDataForm, globalUserPreferences, type);
            }
            return catalogList;
        } catch (error) {
            console.error(`Error en carga inicial: ${error}`);
            return [];
        } finally {
            setBlocked(false);
        }
    }

    /**
     * This TypeScript function asynchronously lists dependent guide stores based on user preferences.
     * @param {any} globalUserPreferences - The `globalUserPreferences` parameter is an object that
     * contains user preferences, including a `guideId` property that is used to fetch dependent guide
     * stores. The `guideId` property is accessed using optional chaining (`?.`) and nullish coalescing
     * (`??`) operators to handle cases where it
     * @param {'from' | 'to'} type - The `type` parameter in the `listDependentGuideStores` function
     * specifies whether the guide stores are listed "from" or "to" a certain point. This parameter helps
     * determine the direction or perspective from which the guide stores are being listed.
     * @returns An array of formatted store data is being returned.
     */
    async listDependentGuideStores(globalUserPreferences: any, type: 'from' | 'to' | 'base'): Promise<any[]> {
        const catalogList = [];
        const body = { guideId: globalUserPreferences.preferences?.guideId ?? null };

        try {
            const data = await this.apiService.listDependetGuideStores(body);
            if (data) {
                data.forEach((element: any) => {
                    catalogList.push(this.formatStoreData(element, true));
                });
            }
        } catch (error) {
            console.error(`Error en lista de guías dependientes: ${error}`);
        }
        return catalogList;
    }

    /**
     * This TypeScript function asynchronously loads coordination catalogs based on user data and
     * preferences.
     * @param {FormGroup} globalUserDataForm - The `globalUserDataForm` parameter is a FormGroup object
     * that represents a form in Angular. FormGroup is a class that tracks the value and validity state of
     * a group of FormControl instances. It's commonly used in Angular reactive forms to manage form data
     * and validation.
     * @param {any} globalUserPreferences - The `globalUserPreferences` parameter is an object containing
     * the user's preferences. It may have a structure like this:
     * @param {'from' | 'to'} type - The `type` parameter in the  function is a
     * string literal type that can only have the values `'from'` or `'to'`. It is used to determine
     * whether to load coordination catalogs for the 'from' or 'to' section in the form.
     * @returns The  function returns an array of catalog items after loading and
     * formatting the coordination data based on the provided parameters and user preferences.
     */
    async loadCoordinationCatalogs(_storeId: string, globalUserDataForm: FormGroup, globalUserPreferences: any, type: 'from' | 'to' | 'base'): Promise<any[]> {
        const formControlName = type === 'from'
            ? 'fromSelectedCoordination'
            : type === 'to'
                ? 'toSelectedCoordination'
                : 'baseSelectedCoordination';

        const storeId = _storeId ?? globalUserDataForm.get(formControlName).value?.store?.storeId ??
            globalUserPreferences?.preferences?.coordinationId ?? null;

        const catalogList = [];

        try {
            const responseStore = await this.getStoreById(storeId);
            if (responseStore) {
                catalogList.push(this.formatStoreData(responseStore, false));
                const data = await this.apiService.getLstTreeChildStores(storeId, "Y");
                const formattedData = data.map((store: any) => this.formatStoreData(store?.store, true));
                catalogList.push(...formattedData);

                globalUserDataForm.patchValue({ [formControlName]: catalogList[0] ?? null });
            }
        } catch (error) {
            console.error(`Error en carga de catálogos de coordinación: ${error}`);
        }
        return catalogList;
    }

    /**
     * The function `formatStoreData` formats store data and includes a flag for redirection.
     * @param {any} store - The `store` parameter is an object that contains the following properties:
     * @param {boolean} isRedirect - A boolean value indicating whether the user should be redirected or
     * not.
     * @returns The `formatStoreData` function is returning an object with a `store` property containing
     * various properties extracted from the `store` object passed as a parameter. The properties being
     * extracted include `storeId`, `addrId`, `addrIdAlt`, `descri`, `shrName`, `status`, `type`,
     * `typeDescrip`, and `isRedirect`. If `store` does not have
     */
    formatStoreData(store: any, isRedirect: boolean) {
        return {
            store: {
                storeId: store?.storeId ?? '',
                addrId: store?.addrId ?? '',
                addrIdAlt: store?.addrIdAlt ?? '',
                descri: store?.descri ?? '',
                shrName: store?.shrName ?? '',
                status: store?.status ?? '',
                type: store?.type ?? '',
                typeDescrip: store?.typeDescrip ?? '',
                isRedirect: isRedirect
            }
        };
    }

    /**
     * The function `getStoreById` asynchronously fetches store data by store ID and handles any errors
     * that may occur.
     * @param {string} _storeId - The `_storeId` parameter is a string representing the unique identifier
     * of the store that you want to retrieve data for.
     * @returns The function `getStoreById` is returning the data fetched from the store with the given
     * storeId. If the fetch is successful, it returns the data. If there is an error during the fetch
     * process, it will log the error and throw a new error message "Failed to fetch store data."
     */
    async getStoreById(_storeId: string) {
        try {
            const result = await this.tovpilCrudService.fetch(this.endPointGetStoreById, { storeId: _storeId });
            if (!result.success) {
                throw new Error(`An error occurred while fetching store data`);
            }
            return result.data;
        } catch (error) {
            console.error(error);
            throw new Error("Failed to fetch store data.");
        }
    }
}
